import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { scrollToTop } from "../../../utils/navigation";
import { Lightbulb, MessageCircle, ArrowLeft } from "lucide-react";
import Dots from "../../ElecCoopLandingPage/Dots";

const ElectricCooperative = () => {
  const navigate = useNavigate();
  const handleCall = () => {
    scrollToTop();
    navigate("/signup");
  };

  return (
    <AnimatePresence>
      <motion.div
        className="flex flex-col min-h-screen bg-gradient-to-br from-yellow-50 via-yellow-100 to-yellow-50 text-customText-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}>
        <motion.header
          className="w-full px-8 py-8 text-center bg-yellow-400 shadow-lg text-customText-100"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7 }}>
          <h1 className="text-4xl font-extrabold tracking-wide">
            BETH AI Module: Electric Cooperative
          </h1>
          <p className="max-w-3xl mx-auto mt-3 text-lg">
            Smarter Compliance, Stronger Cooperatives, Powered by AI.
          </p>
        </motion.header>
        <main className="flex flex-col items-center w-full px-6 mt-12 space-y-16">
          <motion.section
            className="flex flex-col items-center space-y-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}>
            <h2 className="flex items-center justify-center text-3xl font-semibold text-customText-800">
              Time-Saving, Improved Communication, Increased Productivity
            </h2>
            <p className="max-w-5xl mx-auto leading-relaxed text-gray-600">
              To empower Electric Cooperatives (ECs) across the Philippines—from
              Luzon’s northernmost barangays to the remote islands of
              Mindanao—by automating regulatory compliance and enabling
              affordable, reliable electricity. The BETH AI module simplifies
              complex reporting obligations, minimizes compliance-related
              penalties, and equips ECs with real-time monitoring tools that
              optimize service delivery and foster inclusive development.
              Ultimately, this tool is about empowering ECs to focus on
              energizing communities while BETH handles the bureaucratic burden.
            </p>
            <motion.button
              className="flex items-center justify-between gap-4 px-8 py-3 mt-6 font-semibold transition duration-300 transform bg-yellow-400 rounded-full shadow-lg text-customText-100 hover:bg-yellow-500 hover:scale-105"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleCall()}>
              <span>Try it now!</span>
              <MessageCircle className="size-5 " />
            </motion.button>
          </motion.section>

          <motion.div className="flex flex-col w-full ">
            <section className="w-full px-24 space-y-12 text-center">
              <h3 className="text-2xl font-semibold text-gray-700">
                Key Features
              </h3>
              <div className="grid justify-center grid-cols-3 gap-y-4 gap-x-6">
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-yellow-700">
                    <Lightbulb className="mr-3" /> Centralized Compliance Hub
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Consolidate all monthly, quarterly, and annual reporting
                    across ERC, DOE, NEA, BIR, and other regulatory bodies into
                    one intelligent platform.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-yellow-700">
                    <Lightbulb className="mr-3" /> Real-Time Compliance
                    Monitoring
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Track deadlines, ssubmission statuses, and performance
                    metrics acreoss agencies-reducing the risk of penalties or
                    lapses.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-yellow-700">
                    <Lightbulb className="mr-3" /> Permit & License Automation
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Handles renewals, expirations, and submission of permits,
                    licenses, and franchise documents (LGUs, DENR, NCIP, etc.).
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-yellow-700">
                    <Lightbulb className="mr-3" /> Predictive & Prescriptive
                    Insights
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Uses AI to flag potential non-compliance based on historical
                    data (e.g., system loss, overbilling trends, franchise
                    delays).
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-yellow-700">
                    <Lightbulb className="mr-3" /> Dynamic Regulatory Alerts
                    Insights
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Push notifications and dashboards to flag new issuances,
                    upcoming due dates, or changes in compliance guidelines.
                  </p>
                </motion.div>
                <motion.div
                  className="flex-1 p-6 transition-transform transform bg-white rounded-lg shadow-lg hover:scale-105"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}>
                  <h4 className="flex items-center justify-center space-x-2 text-lg font-semibold text-yellow-700">
                    <Lightbulb className="mr-3" /> Human-in-the-Loop Export
                    Support
                  </h4>
                  <p className="mt-2 text-gray-600">
                    Connects ECs with accrediated accountants, tax specialists,
                    and compliance officers via the Babylon2k.org ecosystem.
                  </p>
                </motion.div>
              </div>
            </section>
            <h1 className="mt-32 text-2xl font-bold text-center">
              Compliance Calendar Summary
            </h1>
            <div className="flex flex-col items-center w-full h-full gap-4 mt-6">
              <div className="flex h-full w-fit">
                <div className="h-full px-4 py-6 bg-yellow-400 w-fit whitespace-nowrap rounded-l-3xl">
                  <span className="font-bold">Frequency - Monthly</span>
                </div>
                <div className="flex flex-col pl-4 pr-8 gap-1.5 py-3 justify-center text-sm bg-yellow-200 rounded-r-3xl w-full">
                  <span>
                    <b>Activities</b> - VAT, WESM, labor dues, meter reading,
                    system loss
                  </span>
                  <span>
                    <b>Agencies Involved</b> - BIR, ERC, IEMOP, NEA
                  </span>
                </div>
              </div>
              <div className="flex h-full w-fit">
                <div className="h-full px-4 py-6 bg-yellow-400 whitespace-nowrap w-fit rounded-l-3xl">
                  <span className="font-bold">Frequency - Quarterly</span>
                </div>
                <div className="flex flex-col pl-4 pr-8 gap-1.5 py-3 justify-center text-sm bg-yellow-200 rounded-r-3xl w-full">
                  <span>
                    <b>Activities</b> - RPS, FPPR, procurement, safety
                    compliance
                  </span>
                  <span>
                    <b>Agencies Involved</b> - DOE, DENR, NEA, DOLE
                  </span>
                </div>
              </div>
              <div className="flex h-full w-fit">
                <div className="h-full px-4 py-6 bg-yellow-400 w-fit rounded-l-3xl whitespace-nowrap">
                  <span className="font-bold">Frequency - Annual</span>
                </div>
                <div className="flex flex-col pl-4 pr-8 gap-1.5 py-3 justify-center text-sm bg-yellow-200 rounded-r-3xl w-full">
                  <span>
                    <b>Activities</b> - Fianncial audits, franchise renewals,
                    AGMM reposts compliance
                  </span>
                  <span>
                    <b>Agencies Involved</b> - COA, CDA, ERC, LGUs
                  </span>
                </div>
              </div>
            </div>
            <h1 className="mt-32 text-2xl font-bold text-center">
              Transformational Impact
            </h1>
            <div className="w-full px-40">
              <table className="w-full mt-6 text-left border-collapse">
                <thead className="bg-yellow-300">
                  <tr>
                    <th className="px-5 py-3 font-bold rounded-tl-lg text-customText-900">
                      Dimension
                    </th>
                    <th className="px-5 py-3 font-bold text-customText-900 ">
                      Before BETH
                    </th>
                    <th className="px-5 py-3 font-bold rounded-tr-lg text-customText-900 ">
                      With BETH
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm shadow-md bg-yellow-50">
                  <tr>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Compliance
                    </td>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Manual, fragmented, penalty-prone
                    </td>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Centralized, automated, real-time
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Cost Management
                    </td>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Hidden inefficiencies (system loss, fines)
                    </td>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Predictive cost controls
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Public Trust
                    </td>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Complaint-driven, reactive
                    </td>
                    <td className="px-5 py-3 border-b border-yellow-400">
                      Transparent, data-back governance
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 rounded-bl-lg">
                      Rural Electrification
                    </td>
                    <td className="px-5 py-3">
                      Delayed approvals and executions
                    </td>
                    <td className="px-5 py-3 rounded-br-lg">
                      Streamlined, AI-supported rollout
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h1 className="mt-32 text-2xl font-bold text-center">
              Instructional Video
            </h1>
            <div className="relative flex w-[70%] self-center group justify-center px-8 py-6">
              <video
                src="https://bethaibucket.s3.us-east-1.amazonaws.com/videos/BETH+EC+PORTAL.mp4"
                controls
                className="z-10"></video>
              <span className="absolute top-0 left-0 z-10 font-bold text-green-900 translate-y-10 opacity-0 translate-x-14 group-hover:opacity-100">
                Electric Cooperative Video Demonstration / Presentation
              </span>
              <Dots classname="absolute top-0 right-0 translate-x-1/5 -translate-y-1/5" />
              <Dots classname="absolute bottom-0 left-0 -rotate-180 -translate-x-1/5 translate-y-1/5" />
            </div>
          </motion.div>
        </main>
        <aside className="fixed left-6 bottom-6">
          <Link
            to="/features"
            onClick={scrollToTop}
            className="flex items-center p-4 space-x-2 transition duration-300 bg-white rounded-lg shadow-lg hover:bg-gray-100">
            <ArrowLeft className="text-gray-600" />
            <span className="text-gray-600">Back to Features</span>
          </Link>
        </aside>
        <motion.footer
          className="w-full py-8 text-center text-customText-800"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}>
          <p className="text-sm">
            © {new Date().getFullYear()} Babylon by I-Lead. All rights reserved.
          </p>
        </motion.footer>
      </motion.div>
    </AnimatePresence>
  );
};

export default ElectricCooperative;
